import { mdiCheck, mdiClose, mdiPencil, mdiDelete, mdiPlus } from "@mdi/js";
import Icon from "@mdi/react";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import {
  useToast,
  ToastType,
} from "../../../../context_providers/toast/toast_ctx";
import { bomItemDefaultValue, BOMItemModel } from "../../models/npd_model";
import { ItemModelV2 } from "../../models/item_model";
import { generate_id } from "../../journals/destination_stock_voucher/components/item_row_status";
import { WarehouseModel } from "../../models/warehouse_model";
import { fetchGet } from "../../../../service/api_client";
import WarehouseSearchField from "./newWarehouseSearchField";
import NewItemSearchByIdField from "../../items/item_search_by_ids";

interface BOMItemComponentProps {
  data?: BOMItemModel;
  onSubmit: (data: BOMItemModel) => void;
  edit: boolean;
  disabled: boolean;
  onDelete?: (i: BOMItemModel) => void;
  existingItems?: BOMItemModel[];
}

const BOMItemComponent: FunctionComponent<BOMItemComponentProps> = (props) => {
  const [data, setData] = useState<BOMItemModel>({
    ...bomItemDefaultValue,
  });

  const { showToast } = useToast();
  const { edit, onSubmit, existingItems } = props;
  // const [check, setMdiCheck] = useState(true);
  const [editable, setEditable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [totalUnits, setTotalUnits] = useState(0);
  const [prevUnits, setPrevUnits] = useState(0);
  const [inventory, setInventory] = useState<any[]>([]); // Separate inventory state
  const [availableItems, setAvailableItems] = useState<
    { item_id: number; available_units: number; warehouse_id: number }[]
  >([]);
  const itemIdsRef = useRef<any[]>([]);
  const itemIds = availableItems?.map((item: any) => item.item_id) || [];
  useEffect(() => {
    setEditable(edit);
  }, []);

  useEffect(() => {
    itemIdsRef.current = (availableItems ?? []).map(
      (item: any) => item.item_id
    );
  }, [availableItems]);

  useEffect(() => {
    setEditable(edit);
    if (props.data) {
      setData(props.data);
    } else {
      setData({ ...bomItemDefaultValue });
    }
  }, [edit, props.data]);

  useEffect(() => {
    if (props.data) {
      setData({ ...props.data });
    }
  }, [props.data]);

  useEffect(() => {
    const available = availableItems?.find(
      (item: any) =>
        item.item_id === data.item.id &&
        item.warehouse_id === data.warehouse?.id
    );
    const availableUnits = available?.available_units ?? 0;
    console.log("abailableUnits:", availableUnits);
    setTotalUnits(availableUnits + prevUnits);
  }, [availableItems, data.item, data.warehouse, prevUnits]);

  useEffect(() => {
    // Find the existing item in the list
    const existingItem = existingItems?.find((item) => item.id === data.id);

    // Get the existing unit_no, defaulting to 0 if not found
    const existingUnitNo = existingItem ? Number(existingItem.qty) || 0 : 0;

    // Update state with the existing unit_no
    setPrevUnits(existingUnitNo);
  }, [existingItems, data.id]); // Track changes in existingItems and data.id

  const handleWarehouseSelect = async (selectedWarehouse: WarehouseModel) => {
    setData((prevData) => ({
      ...prevData,
      warehouse: selectedWarehouse,
    }));
    //call function to get list of items in warehouse.
    await getItemsInSelectedWarehouse(selectedWarehouse);
  };

  useEffect(() => {
    if (data.warehouse) {
      getItemsInSelectedWarehouse(data.warehouse);
    }
  }, [data.warehouse]);

  const getItemsInSelectedWarehouse = async (warehouse: WarehouseModel) => {
    const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/items/warehouses?warehouse_id=${warehouse.id}`;
    setLoading(true);

    try {
      const res = await fetchGet(url);
      if (res.success) {
        const { item_data } = res.data;
        console.log("item_data:", item_data, res);

        setAvailableItems(item_data);
      } else {
        showToast({ type: ToastType.error, text: res.error });
        return null;
      }
    } catch (error) {
      showToast({
        type: ToastType.error,
        text: "An error occurred while fetching warehouse stock value",
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updatePrice = async (requestedUnits: number) => {
    if (requestedUnits > totalUnits) {
      showToast({
        type: ToastType.warning,
        text: "Requested units exceed available stock.",
      });
      return;
    } else {
      setData((prevData) => ({
        ...prevData,
        qty: requestedUnits,
      }));
    }
  };
  const onDone = () => {
    if (!data.item || !data.qty || !data.warehouse?.name) {
      showToast({
        type: ToastType.warning,
        text: "Please select Item, Qty and Warehouse",
      });
      return;
    }

    const isDuplicate = (existingItems ?? []).some(
      (entry) =>
        entry.id !== data.id &&
        entry.warehouse?.id === data.warehouse?.id &&
        entry.item?.item_code === data.item?.item_code
    );

    if (isDuplicate) {
      showToast({
        type: ToastType.warning,
        text: "Item already added for the selected warehouse item",
      });
      return;
    }

    onSubmit(data);
  };

  const handleItemSelect = async (selectedItem: ItemModelV2) => {
    setData((prevData) => ({
      ...prevData,
      id: generate_id(),
      item: selectedItem,
      item_id: selectedItem.id,
    }));
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onDone();
      }}
      className="bg-white text-xs h-full"
    >
      <div className="border rounded-lg shadow-sm p-3 text-xs flex flex-col gap-2 bg-white h-full">
        {/* Warehouse */}
        <div>
          <label className="text-gray-400">Warehouse</label>
          <WarehouseSearchField
            type={""}
            value={data.warehouse}
            placeholder=""
            onSelect={handleWarehouseSelect}
          />
        </div>

        {/* Item */}
        <div>
          <label className="text-gray-400">Item</label>
          <NewItemSearchByIdField
            disabled={!editable || props.disabled}
            value={data.item}
            item_ids={itemIds}
            onSelect={handleItemSelect}
          />
        </div>

        {/* Read-only details */}
        <div className="grid grid-cols-2 gap-x-3 gap-y-2">
          <div>
            <span className="text-gray-400">Item Code</span>
            <div className="font-semibold break-words">
              {data?.item?.item_code ?? "--"}
            </div>
          </div>
          <div>
            <span className="text-gray-400">Category</span>
            <div className="font-semibold break-words">
              {data?.item?.category ?? "--"}
            </div>
          </div>
          <div>
            <span className="text-gray-400">Sub Category</span>
            <div className="font-semibold break-words">
              {data?.item?.subcategory ?? "--"}
            </div>
          </div>
          <div>
            <span className="text-gray-400">Component Type</span>
            <div className="font-semibold break-words">
              {data?.item?.components ?? "--"}
            </div>
          </div>
          <div>
            <span className="text-gray-400">Component Sub Type</span>
            <div className="font-semibold break-words">
              {data?.item?.component_sub_type ?? "--"}
            </div>
          </div>
          <div>
            <span className="text-gray-400">Value</span>
            <div className="font-semibold break-words">
              {data?.item?.value ?? "--"}
            </div>
          </div>
          <div>
            <span className="text-gray-400">Footprint</span>
            <div className="font-semibold break-words">
              {data?.item?.footprint ?? "--"}
            </div>
          </div>
          <div>
            <span className="text-gray-400">Manufacturer</span>
            <div className="font-semibold break-words">
              {data?.item?.manufacturer ?? "--"}
            </div>
          </div>
          <div>
            <span className="text-gray-400">Unit Measure</span>
            <div className="font-semibold break-words">
              {data?.item?.unit?.symbol ?? "--"}
            </div>
          </div>
          {/* Quantity */}
          <div>
            <span className="text-gray-400">Quantity</span>
            <input
              value={data.qty ?? 0}
              disabled={!editable}
              placeholder=""
              type="number"
              className="w-full p-1 focus:outline-none border rounded"
              onChange={(e) => {
                const unitNo = Number(e.target.value) || 0;
                if (data.warehouse) {
                  updatePrice(unitNo);
                }
              }}
            />
            <span className="text-[10px] text-gray-500">
              Avail Units:{totalUnits}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 items-center justify-end border-t pt-2 mt-auto">
          {props.data ? (
            !props.disabled && (
              <>
                {editable ? (
                  <>
                    <div
                      onClick={() => {
                        if (!window.confirm("Are you sure !")) return;
                        onDone();
                        setEditable(false);
                      }}
                      className="bg-green-100 p-1 rounded text-green-800 hover:scale-110 transform duration-200 cursor-pointer"
                    >
                      <Icon path={mdiCheck} className="h-4 w-4"></Icon>
                    </div>
                    <div
                      onClick={() => {
                        setData(props.data!);
                        setEditable(false);
                      }}
                      className="bg-red-100 p-1 rounded text-red-800 hover:scale-110 transform duration-200 cursor-pointer"
                    >
                      <Icon path={mdiClose} className="h-4 w-4"></Icon>
                    </div>
                  </>
                ) : (
                  <div
                    onClick={() => setEditable(true)}
                    className="bg-green-100 p-1 rounded text-green-800 hover:scale-110 transform duration-200 cursor-pointer"
                  >
                    <Icon path={mdiPencil} className="h-4 w-4"></Icon>
                  </div>
                )}
                <div
                  onClick={() => {
                    if (!window.confirm("Are you sure !")) return;
                    if (props.onDelete) props.onDelete(data);
                  }}
                  className="bg-red-200 p-1 rounded text-red-800 hover:scale-110 transform duration-200 cursor-pointer"
                >
                  <Icon path={mdiDelete} className="h-4 w-4"></Icon>
                </div>
              </>
            )
          ) : (
            <button className="flex gap-1 text-white text-xs bg-myPrimaryColor rounded-full px-3 py-1">
              <Icon path={mdiPlus} className="h-4 w-4"></Icon> Add
            </button>
          )}
        </div>
      </div>
    </form>
  );
};

export default BOMItemComponent;
